/**
 * Academy floating controls — rendered globally by App.js on every
 * /learn/academy or /academy/lesson route, where the main site chrome
 * (and the universal GlobalBackButton) is hidden by the academy-lockdown.
 *
 *  - Back (top-left): the SAME orange "previous page" back used everywhere
 *    else, plus a tuck-away X (hide it, tap the peek tab to bring it back) so
 *    it's never in the way — on every device.
 *  - Exit Academy (top-right): the single way out to the site.
 *
 * Stable data-testids: academy-back-button, academy-back-dismiss,
 * academy-back-restore, academy-exit-button.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const PILL = {
  position: "fixed",
  top: 12,
  zIndex: 9999,
  padding: "10px 16px",
  borderRadius: 999,
  color: "#fff",
  border: "2px solid rgba(255,255,255,0.4)",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 13,
  letterSpacing: 0.2,
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
};

export default function AcademyExitButton() {
  const navigate = useNavigate();
  const [tucked, setTucked] = useState(false);

  const goBack = () => {
    // One screen back. Fall back to the Academy home if there's no history so
    // the child never dead-ends or accidentally leaves the Academy.
    if (typeof window !== "undefined" && window.history.length > 1) navigate(-1);
    else navigate("/learn/academy");
  };

  return (
    <>
      {tucked ? (
        <button
          type="button"
          data-testid="academy-back-restore"
          onClick={() => setTucked(false)}
          aria-label="Show the back button"
          style={{
            position: "fixed", top: 12, left: 0, zIndex: 9999,
            padding: "8px 8px 8px 4px", borderRadius: "0 999px 999px 0",
            background: "rgba(0,0,0,0.5)", color: "#fff", border: "none", cursor: "pointer",
            display: "inline-flex", alignItems: "center",
          }}
        >
          <span aria-hidden="true">›</span>
        </button>
      ) : (
        <div style={{ position: "fixed", top: 12, left: 12, zIndex: 9999, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            data-testid="academy-back-button"
            onClick={goBack}
            aria-label="Go back one screen"
            style={{
              ...PILL, position: "static",
              background: "linear-gradient(135deg,#F59E0B,#F97316)",
              boxShadow: "0 4px 14px rgba(249,115,22,0.4)",
            }}
          >
            <span aria-hidden="true">←</span>
            <span>Back</span>
          </button>
          <button
            type="button"
            data-testid="academy-back-dismiss"
            onClick={() => setTucked(true)}
            aria-label="Hide the back button"
            title="Hide the back button"
            style={{
              ...PILL, position: "static", padding: "8px 9px",
              background: "rgba(0,0,0,0.5)", border: "2px solid rgba(255,255,255,0.25)", boxShadow: "none",
            }}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>
      )}

      <button
        type="button"
        data-testid="academy-exit-button"
        onClick={() => navigate("/")}
        aria-label="Exit the Learning Academy"
        style={{
          ...PILL,
          right: 12,
          background: "linear-gradient(135deg,#3b82f6,#6366f1)",
          boxShadow: "0 6px 20px rgba(59,130,246,0.45)",
        }}
      >
        <span aria-hidden="true">⏻</span>
        <span>Exit Academy</span>
      </button>
    </>
  );
}
